// The organizational leader's approval evidence, obtained by the Hiring Manager: an uploaded file or a reference.
// Either one attaches it; the leader row's Approve waits for it.
import { useState } from "react";
import { ACTION_LABELS, Field, Flex, Input, Pill, Stack, G6_LABELS } from "@/mc";
import { PKG_RULES, READINESS_LABELS } from "./labels";

export interface EvidenceValue { name?: string; file_id?: string; ref?: string }

export interface EvidenceAttachProps {
  value: EvidenceValue;
  onChange: (value: EvidenceValue) => void;
  onUpload?: ((file: File) => Promise<string>) | undefined;
}

export const hasEvidence = (value: EvidenceValue): boolean => Boolean(value.file_id || value.ref?.trim());
/** The leader's name and the evidence: what Approve waits for. */
export const leaderComplete = (value: EvidenceValue): boolean => (value.name?.trim().length ?? 0) >= 3 && hasEvidence(value);

export function EvidenceAttach({ value, onChange, onUpload }: EvidenceAttachProps) {
  const [uploading, setUploading] = useState(false);
  const [failed, setFailed] = useState<string | undefined>(undefined);
  const pick = async (file: File | undefined) => {
    if (!file || !onUpload) return;
    setUploading(true);
    setFailed(undefined);
    try {
      onChange({ ...value, file_id: await onUpload(file) });
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };
  return (
    <Stack gap={8} className="mc-pkg-ev">
      <Flex gap={8} justify="between">
        <strong>{G6_LABELS.evidence}</strong>
        <Pill>{hasEvidence(value) ? READINESS_LABELS.complete : READINESS_LABELS.missing}</Pill>
      </Flex>
      <p className="mc-hint">{PKG_RULES.signOn}</p>
      <Field label={G6_LABELS.organizationalLeader} required>
        <Input value={value.name ?? ""} onChange={(event) => onChange({ ...value, name: event.target.value })} />
      </Field>
      <Field label={ACTION_LABELS.upload} error={failed}>
        <Input type="file" aria-busy={uploading || undefined} onChange={(event) => { void pick(event.target.files?.[0]); }}  />
      </Field>
      <Field label={G6_LABELS.evidence}>
        <Input value={value.ref ?? ""} onChange={(event) => onChange({ ...value, ref: event.target.value })}  />
      </Field>
    </Stack>
  );
}
